'use client';

import Navbar from '../../../components/Navbar';
import Footer from '../../../components/Footer';

export default function TermsAndConditionsPage() {
    const lastUpdated = "25 Aug, 2026";

    const sections = [
        {
            title: "1. Agreement to Terms",
            content: [
                "These Terms and Conditions constitute a legally binding agreement made between you, whether personally or on behalf of an entity ('you') and DreamAcres ('we', 'us', or 'our'), concerning your access to and use of the DreamAcres platform, website, and associated applications.",
                "By accessing the Site, you acknowledge that you have read, understood, and agree to be bound by all of these Terms and Conditions. If you do not agree with all of these Terms and Conditions, you are prohibited from using the Site and must discontinue use immediately."
            ]
        },
        {
            title: "2. Intellectual Property Rights",
            content: [
                "Unless otherwise indicated, the Site is our proprietary property and all source code, databases, functionality, software, website designs, audio, video, text, photographs, and graphics on the Site (collectively, the 'Content') and the trademarks, service marks, and logos contained therein (the 'Marks') are owned or controlled by us or licensed to us, and are protected by copyright and trademark laws.",
                "Except as expressly provided in these Terms and Conditions, no part of the Site and no Content or Marks may be copied, reproduced, aggregated, republished, uploaded, posted, publicly displayed, encoded, translated, transmitted, distributed, sold, licensed, or otherwise exploited for any commercial purpose whatsoever, without our express prior written permission."
            ]
        },
        {
            title: "3. User Representations",
            content: [
                "By using the Site, you represent and warrant that: (1) all registration info you submit will be true, accurate, current, and complete; (2) you will maintain the accuracy of such info and promptly update it as necessary; (3) you have the legal capacity and you agree to comply with these Terms and Conditions; (4) you are not a minor in the jurisdiction in which you reside; (5) you will not access the Site through automated or non-human means; (6) you will not use the Site for any illegal or unauthorized purpose; and (7) your use of the Site will not violate any applicable law or regulation."
            ]
        },
        {
            title: "4. User Registration & Accounts",
            content: [
                "You may be required to register with the Site to access certain features. You agree to keep your password confidential and will be responsible for all use of your account and password.",
                "We reserve the right to remove, reclaim, or change a username you select if we determine, in our sole discretion, that such username is inappropriate, obscene, or otherwise objectionable.",
                "You are responsible for ensuring that all listings (properties, projects, plots, or services) you post on the platform are authentic, accurate, and comply with local regulatory guidelines (such as RERA)."
            ]
        },
        {
            title: "5. Prohibited Activities",
            content: [
                "You may not access or use the Site for any purpose other than that for which we make the Site available. The Site may not be used in connection with any commercial endeavors except those that are specifically endorsed or approved by us.",
                "Prohibited activities include: systematically retrieving data or other content from the Site to create or compile a database or directory; tricking, defrauding, or misleading us and other users; circumventing or interfering with security-related features; posting fake or deceptive listings; uploading viruses or malicious code; and harassing, annoying, intimidating, or threatening any of our employees or agents."
            ]
        },
        {
            title: "6. Site Management & Disruptions",
            content: [
                "We reserve the right, but not the obligation, to: (1) monitor the Site for violations of these Terms and Conditions; (2) take appropriate legal action against anyone who, in our sole discretion, violates the law or these Terms and Conditions; (3) in our sole discretion and without limitation, refuse, restrict access to, limit the availability of, or disable any of your contributions; and (4) otherwise manage the Site in a manner designed to protect our rights and property and to facilitate the proper functioning of the Site.",
                "We cannot guarantee the Site will be available at all times. We may experience hardware, software, or other problems or need to perform maintenance, resulting in interruptions, delays, or errors. You agree that we have no liability whatsoever for any loss, damage, or inconvenience caused by your inability to access or use the Site during any downtime or discontinuance of the Site."
            ]
        },
        {
            title: "7. Term and Termination",
            content: [
                "These Terms and Conditions shall remain in full force and effect while you use the Site. WITHOUT LIMITING ANY OTHER PROVISION OF THESE TERMS AND CONDITIONS, WE RESERVE THE RIGHT TO, IN OUR SOLE DISCRETION AND WITHOUT NOTICE OR LIABILITY, DENY ACCESS TO AND USE OF THE SITE TO ANY PERSON FOR ANY REASON OR FOR NO REASON.",
                "If we terminate or suspend your account for any reason, you are prohibited from registering and creating a new account under your name, a fake or borrowed name, or the name of any third party, even if you may be acting on behalf of the third party."
            ]
        },
        {
            title: "8. Disclaimer & Limitations of Liability",
            content: [
                "THE SITE IS PROVIDED ON AN AS-IS AND AS-AVAILABLE BASIS. YOU AGREE THAT YOUR USE OF THE SITE AND OUR SERVICES WILL BE AT YOUR SOLE RISK. TO THE FULLEST EXTENT PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, IN CONNECTION WITH THE SITE AND YOUR USE THEREOF.",
                "IN NO EVENT WILL WE OR OUR DIRECTORS, EMPLOYEES, OR AGENTS BE LIABLE TO YOU OR ANY THIRD PARTY FOR ANY DIRECT, INDIRECT, CONSEQUENTIAL, EXEMPLARY, INCIDENTAL, SPECIAL, OR PUNITIVE DAMAGES, INCLUDING LOST PROFIT, LOST REVENUE, LOSS OF DATA, OR OTHER DAMAGES ARISING FROM YOUR USE OF THE SITE, EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES."
            ]
        },
        {
            title: "9. Contact Us",
            content: [
                "In order to resolve a complaint regarding the Site or to receive further information regarding use of the Site, please contact us at:",
                "DreamAcres Tech Private Limited, No 7, 53, Williams Rd, Melapudur, Cantonment, Tiruchirappalli, Tamil Nadu 620001, India.",
                "Email: legal@dreamacres.com"
            ]
        }
    ];

    return (
        <>
            <Navbar />
            <main className="min-h-screen bg-gray-50 pb-16">
                {/* Hero Header */}
                <section className="relative h-24 lg:h-40 overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-primary to-primary-dark" />
                    <div className="absolute inset-0 bg-white/5 backdrop-blur-[1px]" />
                    <div className="absolute inset-0 flex items-center">
                        <div className="container-custom">
                            <h1 className="text-3xl lg:text-3xl font-bold text-white mt-4 mb-2">
                                Terms & Conditions
                            </h1>
                            <p className="text-white/80 text-sm lg:text-base">
                                Last Updated: {lastUpdated}
                            </p>
                        </div>
                    </div>
                </section>

                {/* Main Content Area */}
                <section className="mt-12">
                    <div className="container-custom">
                        <div className="grid lg:grid-cols-12 gap-8 items-start">
                            {/* Left Navigation Widget (Desktop) */}
                            <div className="lg:col-span-4 bg-white p-6 rounded-2xl shadow-sm border border-gray-100 sticky top-24 hidden lg:block">
                                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4 px-2">
                                    Table of Contents
                                </h3>
                                <nav className="space-y-1">
                                    {sections.map((section, idx) => (
                                        <a
                                            key={idx}
                                            href={`#section-${idx}`}
                                            className="block px-3 py-2 text-sm text-gray-600 hover:text-primary hover:bg-primary/5 rounded-lg transition-all"
                                        >
                                            {section.title}
                                        </a>
                                    ))}
                                </nav>
                            </div>

                            {/* Terms Content */}
                            <div className="lg:col-span-8 space-y-8">
                                <div className="bg-white p-8 lg:p-10 rounded-2xl shadow-sm border border-gray-100">
                                    <p className="text-gray-600 mb-8 leading-relaxed">
                                        Welcome to DreamAcres. These Terms and Conditions govern your use of our platform, services, websites,
                                        and applications. By accessing or using the platform, you agree to comply with and be bound by these
                                        Terms. If you do not agree to these Terms, please do not use our services.
                                    </p>

                                    <hr className="border-gray-100 my-8" />

                                    <div className="space-y-10">
                                        {sections.map((section, idx) => (
                                            <div key={idx} id={`section-${idx}`} className="scroll-mt-24">
                                                <h2 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-50 flex items-center gap-3">
                                                    <span className="w-1.5 h-6 bg-primary rounded-full"></span>
                                                    {section.title}
                                                </h2>
                                                <div className="space-y-3">
                                                    {section.content.map((p, pIdx) => (
                                                        <p key={pIdx} className="text-gray-600 leading-relaxed text-sm lg:text-base">
                                                            {p}
                                                        </p>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </>
    );
}
